/**
 * Storefront v7 — the frame the satellite pages wear.
 *
 * About, Contact, the plan catalogue and the order confirmation are pages
 * of the SAME storefront: they read the tenant through the same data hook
 * (useStorefrontData), derive the same theme (deriveTheme), load the same
 * fonts, stand behind the same fail-closed gate (useStorefrontGate) and
 * wear the published website's semantic header/footer when available, or
 * the standard header, phone menu and footer (sections/Shell), on a
 * `.s6-root` that carries the theme's CSS variables. The header sits on
 * the paper — never the over-photo overlay, a satellite has no cover
 * photo — its links lead back to the home page's sections
 * (`/customers[/:slug]?tenant=…#menu`) or to the sibling pages, the cart
 * count is the shared cart's, and the powered-by watermark follows the
 * tenant's public settings. Nothing here is typed per tenant.
 *
 *   <StorefrontFrame pageType="about">{({ brand }) => …}</StorefrontFrame>
 *
 * `children` may be a function of the frame's context (brand, settings,
 * theme, subdomain, storeSlug, …) or plain elements. `gated={false}` keeps
 * the chrome but never blocks or redirects: the order confirmation must
 * reach a customer who has just paid, whatever the storefront toggle says.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../../../../contexts/CartContext';
import useStorefrontGate from '../../../../hooks/useStorefrontGate';
import { ensureStorefrontFont } from '../../../../hooks/useStorefrontFonts';
import StorefrontSEO from '../../mealpass/StorefrontSEO';
import '../storefront-v6.css';
import { deriveTheme, themeToCssVars } from '../theme/deriveTheme';
import useStorefrontData from '../data/useStorefrontData';
import { Header, MobileMenuSheet, Footer } from '../sections/Shell';
import { AnnouncementBar } from '../sections/Shell';
import { RESERVED_SLUGS } from '../Storefront';
import { inferBusinessModel, buildSectionPlan } from '../plan/sectionPlan';
import useWebsitePackage from '../website/useWebsitePackage';
import WebsiteRenderer from '../website/WebsiteRenderer';
import { nativeBrand } from '../website/NativeWebsiteRenderer';
import { hasWebsiteChrome } from '../website/websiteChrome';
import { websiteCatalogue } from '../website/catalogue';
import { CartDrawer } from '../commerce/Cart';
import OrderCutoff, { themeCutoffPlansHref } from '../sections/OrderCutoff';
import { FulfilmentCard } from '../sections/Hero';

/** The tenant query the page arrived with (?tenant= / ?subdomain=), kept on every in-storefront link. */
export const tenantQs = () => {
  const p = new URLSearchParams(window.location.search);
  const keep = new URLSearchParams();
  for (const k of ['tenant', 'subdomain']) { const v = p.get(k); if (v) keep.set(k, v); }
  return keep.toString();
};
export const withTenantQs = (path) => { const qs = tenantQs(); return qs ? `${path}?${qs}` : path; };

/** The store slug from the route, unless the segment is a reserved page word (/customers/about). */
export const useStoreSlug = () => {
  const { storeSlug } = useParams();
  return RESERVED_SLUGS.has(storeSlug) ? undefined : storeSlug;
};

// an ungated page still asks the gate (a hook runs on every render) but is never bounced to /login
const NO_REDIRECT = { noRedirect: true };

export default function StorefrontFrame({ pageType = 'home', ...props }) {
  const storeSlug = useStoreSlug();
  return <StorefrontFramePage key={storeSlug || ''} storeSlug={storeSlug} pageType={pageType} {...props} />;
}

function StorefrontFramePage({ storeSlug, pageType = 'home', title, description, gated = true, seo = true, children }) {
  const navigate = useNavigate();
  const websiteState = useWebsitePackage({ storeSlug });
  const data = useStorefrontData({ storeSlug });
  const { brand: loadedBrand, settings, items, plans, programs, orderable, catering, subdomain, isAuthed, isPreview, loading, error, tenantMeta } = data;
  const brand = useMemo(() => nativeBrand(websiteState.website, loadedBrand), [websiteState.website, loadedBrand]);
  const customChrome = useMemo(() => hasWebsiteChrome(websiteState.website), [websiteState.website]);
  const [contentTheme, setContentTheme] = useState({});
  const gate = useStorefrontGate(subdomain, storeSlug, gated ? undefined : NO_REDIRECT);
  const theme = useMemo(() => ({ ...deriveTheme(brand), ...(customChrome ? contentTheme : {}) }), [brand, contentTheme, customChrome]);
  useEffect(() => {
    [theme.headingFont, theme.bodyFont, theme.scriptFont].forEach(ensureStorefrontFont);
  }, [theme.headingFont, theme.bodyFont, theme.scriptFont]);
  const { cartItems, removeFromCart } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [fulfilmentOpen, setFulfilmentOpen] = useState(false);

  // the same facts as the home page decide which sections exist to link to
  const show = {
    menu: brand.menu_enabled !== false && items.some((item) => !item.out_of_stock),
    plans: brand.mealplans_enabled !== false && plans.some((p) => !p.is_meal_addon && !p.is_delivery_addon),
    passes: brand.mealpass_enabled !== false && programs.length > 0,
    catering: brand.catering_enabled === true && catering.packages.length > 0,
  };
  // THE HOME PAGE RENDERS FROM A PLAN, NOT FROM THIS MAP. For a catering or bakery
  // model the plan drops sections the raw show-map still advertises, so a header link
  // scrolled to an id that was never on the page. The frame reads the same plan.
  const homeModel = useMemo(
    () => inferBusinessModel({ brand, plans, items, cateringPackages: catering.packages }),
    [brand, plans, items, catering.packages],
  );
  const homePlan = useMemo(
    () => buildSectionPlan(homeModel, { hero: true, menu: show.menu, plans: show.plans, passes: show.passes, catering: show.catering }, brand.storefront_sections),
    [homeModel, show.menu, show.plans, show.passes, show.catering, brand.storefront_sections],
  );
  const currency = settings?.currency || programs[0]?.currency || 'CAD';

  // ── the existing rails ────────────────────────────────────────────────
  const homeBase = storeSlug ? `/customers/${storeSlug}` : '/customers';
  const checkoutDest = withTenantQs(storeSlug ? `/customers/${storeSlug}/checkout` : '/customers/checkout');
  const loginDest = withTenantQs(storeSlug ? `/customers/${storeSlug}/login` : '/customers/login');
  const dashboardDest = withTenantQs(storeSlug ? `/customers/${storeSlug}/dashboard` : '/customers/dashboard');
  /** Back to the home page, at one of its sections (the hash names the section id). */
  const goHome = useCallback((section) => {
    const qs = tenantQs();
    navigate({ pathname: homeBase, search: qs ? `?${qs}` : '', hash: section ? `#${section}` : '' });
  }, [navigate, homeBase]);
  const goToPage = useCallback((page) => navigate(withTenantQs(`${homeBase}/${page}`)), [navigate, homeBase]);
  const goAccount = () => navigate(isAuthed ? dashboardDest : loginDest);
  const goOrderOnline = () => {
    const parts = [];
    if (subdomain) parts.push(`tenant=${encodeURIComponent(subdomain)}`);
    if (storeSlug) parts.push(`store_slug=${encodeURIComponent(storeSlug)}`);
    parts.push('src=online');
    navigate(`/order?${parts.join('&')}`);
  };
  const orderLabel = settings?.store_hours_enabled && settings?.is_open === false
    ? (settings?.next_open?.time ? `Schedule ahead, opens ${settings.next_open.time}` : 'Schedule an order')
    : 'Order now';
  const accountLabel = isAuthed ? 'My dashboard' : 'Sign in';
  const websiteNavigate = (action, target) => {
    if (action === 'navigate.page') goHome(target === websiteState.website.package.manifest.entry ? '' : target);
    if (action === 'navigate.section') goHome(target);
  };
  const websiteAction = action => {
    if (action === 'commerce.cart.open' || action === 'commerce.checkout') setCartOpen(true);
    else if (action === 'customer.signIn') goToPage('login');
    else if (action === 'customer.portal') goToPage('dashboard');
    else if (action === 'catalogue.directory') goToPage('menu');
    else if (action === 'fulfilment') setFulfilmentOpen(true);
    else if (action === 'commerce.configure') goHome('plans');
  };
  const navItems = [
    { key: 'directory', label: 'Menu Directory', active: pageType === 'menu', onClick: () => goToPage('menu') },
    show.menu && homePlan.includes('menu') && { key: 'menu', label: 'Menu', onClick: () => goHome('menu') },
    show.plans && homePlan.includes('plans') && { key: 'plans', label: 'Plans', active: pageType === 'plans', onClick: () => goHome('plans') },
    show.passes && homePlan.includes('passes') && { key: 'passes', label: 'Passes', onClick: () => goHome('passes') },
    show.catering && homePlan.includes('catering') && { key: 'catering', label: 'Catering', onClick: () => goHome('catering') },
    { key: 'about', label: 'About', active: pageType === 'about', onClick: () => goToPage('about') },
    { key: 'contact', label: 'Contact', active: pageType === 'contact', onClick: () => goToPage('contact') },
    { key: 'account', label: accountLabel, onClick: goAccount },
    orderable.sellableCount > 0 && { key: 'order', label: orderLabel, primary: true, onClick: goOrderOnline },
  ].filter(Boolean);

  // the page's own title / description sit BELOW the merchant's seo_title / seo_description
  const seoBrand = useMemo(() => {
    if (!title && !description) return brand;
    const b = { ...brand };
    if (title && !b.seo_title) b.seo_title = title;
    if (description && !b.seo_description) b.seo_description = description;
    return b;
  }, [brand, title, description]);

  // ── gate (fail closed; the hook redirects) — the home page's own early return ──
  if (gated && (gate.checking || !gate.allowed)) {
    return <div className="min-h-screen" style={{ background: theme.paper }} data-testid="s6-frame-gate" />;
  }

  // the chrome waits for the brand (the first fetch) so the header never flashes a placeholder initial
  const chromeReady = !websiteState.loading && !websiteState.error && (!!brand.brand_name || !loading);
  const ctx = {
    brand, settings, theme, subdomain, storeSlug, isAuthed, loading: loading || websiteState.loading, error, plans, items, programs, tenantMeta, currency, show,
    navigate, goHome, goToPage, checkoutDest, loginDest,
  };
  return (
    <div className="s6-root min-h-screen flex flex-col" style={themeToCssVars(theme)} data-testid="s6-frame" data-page={pageType}>
      {seo && (
        <StorefrontSEO brand={seoBrand} subdomain={subdomain} pageType={pageType} programs={programs} mealPlans={plans} settings={settings} isConceptPreview={tenantMeta?.is_concept_preview === true} />
      )}
      {chromeReady && brand.show_marquee !== false && <AnnouncementBar items={brand.marquee_items} />}
      {customChrome && chromeReady && <OrderCutoff browsePlansHref={themeCutoffPlansHref(websiteState.website, withTenantQs(homeBase))} plans={plans} onChoose={() => goHome('plans')} onRefresh={data.refetch} />}
      {customChrome && chromeReady ? <WebsiteRenderer
        website={websiteState.website} onContentTheme={setContentTheme}
        catalogue={websiteCatalogue({ plans, items, programs, catering, orderable, currency })}
        onAction={websiteAction} onNavigate={websiteNavigate}
        frameContent={<main className="flex-1">{typeof children === 'function' ? children(ctx) : children}</main>}
      /> : <>
      {chromeReady ? (
        <Header
          brand={brand} theme={theme} items={navItems} overlay={false} cartCount={cartItems.length}
          onCart={() => navigate(checkoutDest)} onSearch={show.menu ? () => goHome('menu') : null}
          onAccount={goAccount} accountLabel={accountLabel} onMenu={() => setMenuOpen(true)} menuOpen={menuOpen}
          onHome={() => goHome()}
        />
      ) : (
        <div className="h-16 md:h-[72px]" aria-hidden="true" data-testid="s6-frame-header-wait" />
      )}
      {/* Keep gated content unmounted until its final parent (native or custom) is ready. */}
      <main className="flex-1">{!chromeReady && gated
        ? !websiteState.error && <p role="status" className="s6-container py-12">Loading the website…</p>
        : typeof children === 'function' ? children(ctx) : children}</main>
      {chromeReady && <Footer brand={brand} settings={settings} items={navItems} poweredBy={!!settings?.powered_by_plateos} />}
      <MobileMenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} brand={brand} items={navItems} />
      </>}
      {websiteState.error && <div role="alert" className="s6-container py-6"><p>{websiteState.error}</p><button className="s6-btn mt-3" type="button" onClick={() => window.location.reload()}>Try again</button></div>}
      {customChrome && <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} cartItems={cartItems}
        onRemoveCartItem={removeFromCart} currency={currency} settings={settings} subdomain={subdomain}
        onCheckout={() => { setCartOpen(false); navigate(checkoutDest); }} onOrderDishes={goOrderOnline} />}
      {customChrome && fulfilmentOpen && <div role="dialog" aria-modal="true" aria-label="Delivery and pickup" className="fixed inset-0 z-[70] bg-[var(--s6-paper)] overflow-auto p-6">
        <button type="button" className="s6-btn" onClick={() => setFulfilmentOpen(false)}>Close</button>
        <FulfilmentCard brand={brand} settings={settings} subdomain={subdomain} storeSlug={storeSlug}
          isPreview={isPreview} currency={currency} overlap={false}
          pickupAvailable={!!settings?.pickup_address} deliveryAvailable={brand.show_delivery_checker === true}
          onOrder={() => { setFulfilmentOpen(false); orderable.sellableCount > 0 ? goOrderOnline() : goHome('plans'); }} />
      </div>}
    </div>
  );
}
